import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Button, Select, DatePicker, Space, Tag, Popconfirm, message, Typography, Form, Segmented, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';

// Manage Badges (agent summary Actions menu, CanAssignBadge >= 1): every badge
// the employee has been given, expired ones included, with add / edit / delete.
// The same badge can be given more than once; Year-Month only says when it applies.
export default function ManageBadgesModal({ open, userId, userName, onClose, onChanged }) {
  const [rows,      setRows]      = useState([]);
  const [badges,    setBadges]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  // Year-Month can be a month ('2026-08') or a whole year ('2026').
  const [periodMode, setPeriodMode] = useState('month');
  // The row being edited in place: {id, badge_id, mode, year_month, expires_on}.
  const [edit,       setEdit]       = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/users/${userId}/badges`, { params: { all: 1 } });
      setRows(res.data);
    } catch {
      message.error('Failed to load badges.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!open) return;
    setEdit(null);
    setPeriodMode('month');
    form.resetFields();
    load();
    api.get('/api/badges', { params: { active_only: 1 } })
      .then((r) => setBadges(r.data))
      .catch(() => {});
  }, [open, load, form]);

  const startEdit = (r) => {
    const ym = (r.YearMonth || '').trim();
    const isYear = ym.length === 4;
    setEdit({
      id:         r.ID,
      badge_id:   r.BadgeID,
      mode:       isYear ? 'year' : 'month',
      year_month: ym ? dayjs(isYear ? `${ym}-01-01` : `${ym}-01`) : null,
      expires_on: r.ExpiresOn ? dayjs(r.ExpiresOn) : null,
      special_note: r.SpecialNote || '',
    });
  };

  const saveEdit = async () => {
    if (!edit?.badge_id) { message.warning('Pick a badge.'); return; }
    setEditSaving(true);
    try {
      await api.put(`/api/user-badges/${edit.id}`, {
        badge_id:   edit.badge_id,
        year_month: edit.year_month ? edit.year_month.format(edit.mode === 'year' ? 'YYYY' : 'YYYY-MM') : null,
        expires_on: edit.expires_on ? edit.expires_on.format('YYYY-MM-DD') : null,
        special_note: edit.special_note || null,
      });
      message.success('Badge updated.');
      setEdit(null);
      await load();
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setEditSaving(false);
    }
  };

  const add = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      await api.post(`/api/users/${userId}/badges`, {
        badge_id:   values.badge_id,
        year_month: values.year_month ? values.year_month.format(periodMode === 'year' ? 'YYYY' : 'YYYY-MM') : null,
        expires_on: values.expires_on ? values.expires_on.format('YYYY-MM-DD') : null,
        special_note: values.special_note || null,
      });
      message.success('Badge added.');
      form.resetFields();
      setPeriodMode('month');
      await load();
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/user-badges/${id}`);
      message.success('Badge removed.');
      if (edit?.id === id) setEdit(null);
      await load();
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Delete failed.');
    }
  };

  const badgeOptions = badges.map((b) => ({ value: b.ID, label: b.Name }));
  // In the row being edited, its own badge stays selectable even if it has
  // since been made inactive.
  const optionsFor = (r) => (badges.some((b) => b.ID === r.BadgeID)
    ? badgeOptions
    : [...badgeOptions, { value: r.BadgeID, label: `${r.BadgeName} (inactive)` }]);
  const isEditing = (r) => edit?.id === r.ID;
  const setE = (patch) => setEdit((prev) => ({ ...prev, ...patch }));

  const today = dayjs().format('YYYY-MM-DD');
  const columns = [
    { title: 'Badge', dataIndex: 'BadgeName', key: 'BadgeName',
      render: (v, r) => (isEditing(r)
        ? <Select size="small" value={edit.badge_id} options={optionsFor(r)} showSearch optionFilterProp="label"
                  style={{ width: '100%', minWidth: 180 }} onChange={(id) => setE({ badge_id: id })} />
        : <>{v}{!r.BadgeIsActive && <Tag style={{ marginLeft: 6 }}>inactive</Tag>}</>) },
    { title: 'Year-Month', dataIndex: 'YearMonth', key: 'YearMonth', width: 200,
      render: (v, r) => (isEditing(r)
        ? (
          <Space size={4}>
            <Segmented size="small" value={edit.mode}
                       onChange={(m) => setE({ mode: m, year_month: null })}
                       options={[{ label: 'Month', value: 'month' }, { label: 'Year', value: 'year' }]} />
            <DatePicker size="small" picker={edit.mode} value={edit.year_month}
                        format={edit.mode === 'year' ? 'YYYY' : 'YYYY-MM'} style={{ width: 95 }}
                        onChange={(d) => setE({ year_month: d })} />
          </Space>
        )
        : (v || '—')) },
    { title: 'Expires On', dataIndex: 'ExpiresOn', key: 'ExpiresOn', width: 150,
      render: (v, r) => (isEditing(r)
        ? <DatePicker size="small" value={edit.expires_on} format="YYYY-MM-DD" style={{ width: 130 }}
                      onChange={(d) => setE({ expires_on: d })} />
        : !v ? '—'
          : <>{String(v).slice(0, 10)}{String(v).slice(0, 10) < today && <Tag color="red" style={{ marginLeft: 6 }}>expired</Tag>}</>) },
    { title: 'Special Note', dataIndex: 'SpecialNote', key: 'SpecialNote', width: 200,
      render: (v, r) => (isEditing(r)
        ? <Input size="small" value={edit.special_note} maxLength={100} onChange={(e) => setE({ special_note: e.target.value })} />
        : (v || '—')) },
    { title: 'Assigned By', dataIndex: 'AssignedByName', key: 'AssignedByName', width: 130, render: (v) => v || '—' },
    {
      title: '', key: 'actions', width: 90, align: 'right',
      render: (_, r) => (isEditing(r)
        ? (
          <Space size={4}>
            <Button size="small" type="primary" icon={<CheckOutlined />} loading={editSaving} onClick={saveEdit} />
            <Button size="small" icon={<CloseOutlined />} onClick={() => setEdit(null)} />
          </Space>
        )
        : (
          <Space size={4}>
            <Button size="small" icon={<EditOutlined />} disabled={!!edit} onClick={() => startEdit(r)} />
            <Popconfirm title="Remove this badge?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(r.ID)}>
              <Button size="small" danger icon={<DeleteOutlined />} disabled={!!edit} />
            </Popconfirm>
          </Space>
        )),
    },
  ];

  return (
    <Modal maskClosable={false}
      title={`Manage Badges${userName ? ` — ${userName}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={1080}
      destroyOnHidden
    >
      <Typography.Text strong>Add Badge</Typography.Text>
      <Form form={form} layout="inline" style={{ margin: '8px 0 16px', rowGap: 8 }}>
        <Form.Item name="badge_id" rules={[{ required: true, message: 'Pick a badge' }]}>
          <Select placeholder="Badge" options={badgeOptions} showSearch optionFilterProp="label" style={{ width: 220 }} />
        </Form.Item>
        <Form.Item>
          <Segmented
            size="small"
            value={periodMode}
            onChange={(v) => { setPeriodMode(v); form.setFieldValue('year_month', null); }}
            options={[{ label: 'Month', value: 'month' }, { label: 'Year', value: 'year' }]}
          />
        </Form.Item>
        <Form.Item name="year_month">
          <DatePicker
            picker={periodMode}
            placeholder={periodMode === 'year' ? 'Year (optional)' : 'Year-Month (optional)'}
            format={periodMode === 'year' ? 'YYYY' : 'YYYY-MM'}
            style={{ width: 170 }}
          />
        </Form.Item>
        <Form.Item name="expires_on">
          <DatePicker placeholder="Expires On (optional)" format="YYYY-MM-DD" style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="special_note">
          <Input placeholder="Special Note (optional)" maxLength={100} style={{ width: 260 }} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} loading={saving} onClick={add}>Add</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
        rowClassName={(r) => (isEditing(r) ? 'ant-table-row-selected' : '')}
        locale={{ emptyText: 'No badges yet.' }}
      />
    </Modal>
  );
}
